/**
 * custom-dashboard Agent の LLM コスト・運用確認ビューの Query Hook（T-0042）
 */

import { useQuery } from '@tanstack/react-query';
import { useAuthHeader } from '@/hooks/useAuth';
import {
  getDashboardLlmCostAuthenticated,
  getDashboardLlmCostJobLogUrlAuthenticated,
  type DashboardLlmCostParams,
} from '@/lib/api/dashboard/llmCost';
import { dashboardLlmCostKeys } from '../keys';

/** 実行中ジョブの経過を追えるよう、他画面より短い間隔で再取得する */
const REFETCH_INTERVAL_MS = 60_000;

export function useDashboardLlmCost(params: DashboardLlmCostParams) {
  const { getAuthHeader, isLoading: authHeaderLoading, isAuthorized } = useAuthHeader();

  return useQuery({
    queryKey: dashboardLlmCostKeys.data(
      params.start_date,
      params.end_date,
      params.granularity ?? '',
      params.account_code ?? '',
      params.tab_id ?? '',
      params.status ?? '',
    ),
    queryFn: async () => {
      const authHeaders = getAuthHeader();
      return getDashboardLlmCostAuthenticated(authHeaders, params);
    },
    enabled: !authHeaderLoading && isAuthorized && !!params.start_date && !!params.end_date,
    staleTime: REFETCH_INTERVAL_MS,
    refetchInterval: REFETCH_INTERVAL_MS,
  });
}

/**
 * ジョブログ（S3）の presigned URL を取得する関数を返す（ジョブ一覧のダウンロードボタン用。T-0043）。
 * 押下時だけ呼ぶため useQuery にはしない。
 */
export function useFetchLlmCostJobLogUrl() {
  const { getAuthHeader } = useAuthHeader();

  return async (jobId: string): Promise<string> => {
    const response = await getDashboardLlmCostJobLogUrlAuthenticated(getAuthHeader(), jobId);
    return response.url;
  };
}
